// 🔹 React Core
import React, { FC } from 'react';

// 🔹 Drag & Drop Libraries
import { CSS } from '@dnd-kit/utilities';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';

// 🔹 Internal Components
import { ExerciseCard } from './ExerciseCard';

// 🔹 Custom Hooks
import { useProgramStore } from './useProgramStore';

interface ExerciseLibraryProps {
  store: ReturnType<typeof useProgramStore>;
  isOpen?: boolean;
  onClose?: () => void;
}

const MUSCLE_GROUPS = [
  { value: 'all', label: 'All' },
  { value: 'lower-body', label: 'Lower Body' },
  { value: 'upper-body', label: 'Upper Body' },
  { value: 'full-body', label: 'Full Body' },
  { value: 'core', label: 'Core' },
];

export const EXERCISE_TYPES = [
  { value: 'all', label: 'All' },
  { value: 'warmup', label: 'Warmup' },
  { value: 'main', label: 'Main' },
  { value: 'cooldown', label: 'Cooldown' },
  { value: 'fitness', label: 'Fitness' },
  { value: 'cardio', label: 'Cardio' },
];

interface SortableExerciseItemProps {
  ex: any;
  index: number;
  letter: string;
  draggedExercise: any;
}

const SortableExerciseItem: FC<SortableExerciseItemProps> = ({ ex, index, letter }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: ex._id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ExerciseCard exercise={ex} index={index} letterKey={letter} isDragging={false} />
    </div>
  );
};

export const ExerciseLibrary: FC<ExerciseLibraryProps> = ({ store, isOpen, onClose }) => {
  const {
    search,
    setSearch,
    muscleFilter,
    setMuscleFilter,
    typeFilter,
    setTypeFilter,
    filteredExercises,
    exercises,
    draggedExercise,
    draggedOverSlot,
  } = store;

  return (
    <aside id="exercise-library" className={`exercise-library ${isOpen ? 'open' : ''}`}>
      <div className="lib-header">
        <h2>Exercise Library</h2>
        <button className="lib-close-btn" onClick={onClose}>
          &times;
        </button>
      </div>

      <div className="lib-search">
        <input
          type="text"
          placeholder="Search movements..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className="search-clear-btn" onClick={() => setSearch('')}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
      </div>

      <div className="lib-filters">
        <div className="lib-filter-group">
          <label>Muscle Groups</label>
          <div className="pill-container">
            {MUSCLE_GROUPS.map((g) => (
              <button
                key={g.value}
                className={`filter-pill ${muscleFilter === g.value ? 'active' : ''}`}
                onClick={() => setMuscleFilter(g.value)}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>

        <div className="lib-filter-group">
          <label>Exercise Type</label>
          <div className="pill-container">
            {EXERCISE_TYPES.map((t) => (
              <button
                key={t.value}
                className={`filter-pill ${typeFilter === t.value ? 'active' : ''} ${draggedOverSlot && draggedExercise?.type.some((et) => et.toLowerCase() === t.value) ? 'pulse' : ''}`}
                onClick={() => setTypeFilter(t.value)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="lib-list-container">
        {Object.keys(filteredExercises)
          .sort()
          .map((letter) => (
            <div key={letter} className="lib-letter-group">
              <h3 className="lib-letter-header">{letter}</h3>
              <div className="lib-letter-items">
                <SortableContext
                  items={filteredExercises[letter].map((ex) => ex._id)}
                  strategy={verticalListSortingStrategy}
                >
                  {filteredExercises[letter].map((ex, index) => (
                    <SortableExerciseItem
                      key={ex._id}
                      ex={ex}
                      index={index}
                      letter={letter}
                      draggedExercise={draggedExercise}
                    />
                  ))}
                </SortableContext>
              </div>
            </div>
          ))}
      </div>

      <div className="lib-footer">
        <p>Drag any exercise into a day slot</p>
        <span>{exercises.length} movements available</span>
      </div>
    </aside>
  );
};
